/* @jsx h */
import { defineComponent, h, ref, computed, onMounted, onUnmounted } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { setTitle, formatPriceInLocale } from "@lemmonhq/orders";
import { useTrpc } from "@app/vue/providers/index";
import type { LiveStatus } from "@app/api/trpc/routers/live-status";

/**
 * Live status — designed for a phone held one-handed on the floor, and for a
 * tablet propped at the pass. Everything is legible at arm's length, the layout
 * is single-column on mobile, and nothing needs horizontal scrolling.
 *
 * Refresh follows the app's existing convention (ShopConnection's 30s interval)
 * rather than introducing a second polling style, and pauses while the tab is
 * hidden so a backgrounded phone is not polling all service.
 */

const REFRESH_MS = 30_000;

export const LiveStatusPage = defineComponent({
  name: "LiveStatusPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();

    const data = ref<LiveStatus | null>(null);
    const error = ref<string | null>(null);
    const loading = ref(true);
    const lastUpdated = ref<Date | null>(null);
    let timer: ReturnType<typeof setInterval> | null = null;

    const load = async () => {
      try {
        data.value = (await trpc.liveStatus.get.query()) as LiveStatus;
        error.value = null;
        lastUpdated.value = new Date();
      } catch (e) {
        // Keep the last good numbers on screen — a stale figure with a warning
        // beats an empty page mid-service.
        error.value = e instanceof Error ? e.message : String(e);
      } finally {
        loading.value = false;
      }
    };

    const startPolling = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(() => void load(), REFRESH_MS);
    };

    /**
     * Load, then restart the interval — the same shape ShopConnection.refreshData
     * uses. Without the restart a manual refresh can be followed a few hundred
     * ms later by a scheduled tick, and the two overlapping loads race to write
     * data/error/lastUpdated, which can pair a stale body with a fresh timestamp.
     */
    const refresh = async () => {
      await load();
      startPolling();
    };

    // Foregrounding refreshes immediately and restarts the interval, so the
    // first thing seen after unlocking a phone is current.
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        void refresh();
      } else if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };

    onMounted(() => {
      setTitle("Live");
      void load();
      startPolling();
      document.addEventListener("visibilitychange", onVisibility);
    });

    onUnmounted(() => {
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    });

    const currency = computed(() => data.value?.currency ?? "CHF");
    const money = (value: number | undefined) =>
      formatPriceInLocale(value ?? 0, true, 2, currency.value);

    const occupancyPct = computed(() => {
      const tables = data.value?.tables;
      if (!tables || tables.total === 0) return 0;
      return Math.round((tables.busy / tables.total) * 100);
    });

    /**
     * Tile accents. Colour carries meaning here, it is not decoration:
     * each one marks WHAT KIND of number this is, so a glance finds the money
     * without reading labels, and a problem is red before it is read at all.
     *
     *   floor   — occupancy and covers, the neutral "how busy are we"
     *   money   — settled cash. The figure the manager looks for first.
     *   pending — value that is not yours yet (open tables, still to arrive)
     *   good    — completed, nothing to do
     *   alert   — needs action now (overdue parties, no-shows)
     *
     * Hues are the validated chart palette, so tiles and charts read as one
     * system rather than two colour schemes on the same page.
     */
    type Tone = "floor" | "money" | "pending" | "good" | "alert" | "muted";
    const TONES: Record<Tone, { bar: string; icon: string; chip: string }> = {
      floor: {
        bar: "bg-[#2f7ded]",
        icon: "text-[#2f7ded]",
        chip: "bg-[#2f7ded]/12",
      },
      money: {
        bar: "bg-[#0f9488]",
        icon: "text-[#0f9488]",
        chip: "bg-[#0f9488]/12",
      },
      pending: {
        // Purple, not amber. Amber (#b8860b) against the alert orange scored
        // ΔE 0.8 for deuteranopia in the dataviz validator — "not yours yet"
        // and "needs action" would have been the same colour to a colourblind
        // reader. This hue is from the same validated chart palette.
        bar: "bg-[#9d4edd]",
        icon: "text-[#9d4edd]",
        chip: "bg-[#9d4edd]/14",
      },
      good: {
        bar: "bg-[#0f9488]",
        icon: "text-[#0f9488]",
        chip: "bg-[#0f9488]/12",
      },
      alert: {
        bar: "bg-[#e2622c]",
        icon: "text-[#e2622c]",
        chip: "bg-[#e2622c]/14",
      },
      muted: {
        bar: "bg-base-content/30",
        icon: "text-base-content/55",
        chip: "bg-base-content/5",
      },
    };

    /** Big single-number tile. Sized for a glance, not for study. */
    const Stat = (
      label: string,
      value: string,
      opts: { sub?: string; tone?: Tone; icon?: string } = {},
    ) => {
      const tone = TONES[opts.tone ?? "floor"];
      return (
        <div class="relative flex min-h-[104px] flex-col justify-between overflow-hidden rounded-2xl border  bg-base-100 p-4 pl-5 shadow-sm">
          {/* A 3px spine rather than a tinted card: it reads at a glance without
              washing the whole tile and losing the number's contrast. */}
          <span class={["absolute inset-y-0 left-0 w-[3px]", tone.bar]} />
          <div class="flex items-start justify-between gap-2">
            <span class="text-[11px] font-semibold uppercase tracking-wider text-accent/70">
              {label}
            </span>
            {opts.icon && (
              <span
                class={[
                  "grid h-6 w-6 shrink-0 place-items-center rounded-lg",
                  tone.chip,
                ]}
              >
                <span
                  class={[
                    "material-symbols-outlined text-[15px] leading-none",
                    tone.icon,
                  ]}
                >
                  {opts.icon}
                </span>
              </span>
            )}
          </div>
          <span class="mt-2 text-[28px] font-bold leading-none tabular-nums text-accent">
            {value}
          </span>
          <span
            aria-hidden={opts.sub ? undefined : "true"}
            class={[
              "mt-1.5 min-h-4 text-xs text-accent/70",
              !opts.sub && "invisible",
            ]}
          >
            {opts.sub ?? "\u00a0"}
          </span>
        </div>
      );
    };

    return () => {
      if (loading.value && !data.value) {
        return (
          <div class="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 8 }, (_, i) => (
              <div key={i} class="h-28 animate-pulse rounded-2xl bg-base-200" />
            ))}
          </div>
        );
      }

      const d = data.value;
      const res = d?.reservations;

      return (
        <div class="flex-1 flex flex-col gap-4 p-3 pb-10 sm:p-4">
          <header class="flex flex-wrap items-baseline justify-between gap-2 border-b  pb-3">
            <div>
              <h2 class="text-lg font-bold text-accent sm:text-xl">
                {t("reports.live.title")}
              </h2>
              <p class="mt-1 flex items-center gap-1.5 text-sm text-base-content/70">
                {/* A live pulse beats the word "open" in grey — service state is
                    the first thing to read and it should be pre-attentive. */}
                <span
                  class={[
                    "relative inline-flex h-2 w-2 shrink-0 rounded-full",
                    d?.shift_open ? "bg-[#0f9488]" : "bg-base-content/30",
                  ]}
                >
                  {d?.shift_open && (
                    <span class="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#0f9488] opacity-60" />
                  )}
                </span>
                {d?.shift_open
                  ? t("reports.live.shift_open")
                  : t("reports.live.shift_closed")}
              </p>
            </div>
            {/* Was btn-ghost: on a page of white cards it had no edge at all.
                The timestamp doubles as the label, so it needs to read as a
                button and not as a caption. */}
            <button
              type="button"
              class="btn btn-sm gap-1  bg-base-100 text-base-content hover:border-primary hover:bg-primary/10 hover:text-primary"
              onClick={() => void refresh()}
            >
              <span class="material-symbols-outlined text-base">refresh</span>
              {lastUpdated.value
                ? new Intl.DateTimeFormat(undefined, {
                    hour: "2-digit",
                    minute: "2-digit",
                  }).format(lastUpdated.value)
                : t("reports.live.refresh")}
            </button>
          </header>

          {error.value && (
            <div class="rounded-xl border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-base-content/80">
              {t("reports.live.stale")}
            </div>
          )}

          {/* Service right now. Two columns even on the narrowest phone —
              these are short numbers and pairing them halves the scrolling. */}
          <section>
            <h3 class="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
              {t("reports.live.section.floor")}
            </h3>
            <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {Stat(
                t("reports.live.tables_busy"),
                `${d?.tables?.busy ?? 0}/${d?.tables?.total ?? 0}`,
                {
                  tone: "floor",
                  icon: "table_restaurant",
                  sub: t("reports.live.occupancy").replace(
                    "{pct}",
                    String(occupancyPct.value),
                  ),
                },
              )}
              {Stat(
                t("reports.live.tables_free"),
                String(d?.tables?.free ?? 0),
                {
                  // Free tables are capacity, not a problem — muted so busy/money
                  // keep the eye.
                  tone: "muted",
                  icon: "check_circle",
                },
              )}
              {Stat(
                t("reports.live.seated_guests"),
                String(res?.seated_guests ?? 0),
                {
                  tone: "floor",
                  icon: "groups",
                  sub: t("reports.live.from_reservations").replace(
                    "{count}",
                    String(res?.seated ?? 0),
                  ),
                },
              )}
              {Stat(t("reports.live.upcoming"), String(res?.upcoming ?? 0), {
                tone: "pending",
                icon: "schedule",
                sub: t("reports.live.covers").replace(
                  "{count}",
                  String(res?.upcoming_guests ?? 0),
                ),
              })}
            </div>
          </section>

          {/* Money. The two bases are separated by section, labelled, and each
              carries a one-line note saying what it is — settled vs expected.
              They are never summed into a single headline.

              Absent entirely for non-managers: the server strips these fields
              rather than the UI hiding them, so there is nothing to reveal. */}
          {d?.collected_today && (
            <section>
              <h3 class="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
                {t("reports.live.section.money")}
              </h3>
              <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
                {Stat(
                  t("reports.live.collected"),
                  money(d?.collected_today?.total),
                  {
                    // Settled money — the one figure a manager hunts for.
                    tone: "money",
                    icon: "payments",
                    sub: t("reports.live.collected_sub"),
                  },
                )}
                {Stat(
                  t("reports.live.on_tables"),
                  money(d?.open_tables?.amount),
                  {
                    // Not yours yet: deliberately a different hue from collected
                    // so the two money bases never read as the same thing.
                    tone: "pending",
                    icon: "hourglass_top",
                    sub: t("reports.live.on_tables_sub"),
                  },
                )}
                {Stat(t("reports.live.tips"), money(d?.collected_today?.tips), {
                  tone: "money",
                  icon: "volunteer_activism",
                })}
                {Stat(
                  t("reports.live.open_orders"),
                  String(d?.open_tables?.order_count ?? 0),
                  {
                    tone: "muted",
                    icon: "receipt_long",
                    sub: t("reports.live.across_tables").replace(
                      "{count}",
                      String(d?.open_tables?.table_count ?? 0),
                    ),
                  },
                )}
              </div>
              <p class="mt-2 text-[11px] leading-relaxed text-base-content/70">
                {t("reports.live.money_note")}
              </p>
            </section>
          )}

          {/* Today's bookings, including the two numbers a manager acts on:
              parties that have not shown, and no-shows already marked. */}
          <section>
            <h3 class="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
              {t("reports.live.section.reservations")}
            </h3>
            <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {Stat(t("reports.live.today"), String(res?.today ?? 0), {
                tone: "floor",
                icon: "event",
                sub: t("reports.live.covers").replace(
                  "{count}",
                  String(res?.today_guests ?? 0),
                ),
              })}
              {Stat(t("reports.live.completed"), String(res?.completed ?? 0), {
                tone: "good",
                icon: "task_alt",
              })}
              {/* Alert colour only when the count is non-zero — a red 0 trains
                  people to ignore red. At zero these are just quiet numbers. */}
              {Stat(t("reports.live.overdue"), String(res?.overdue ?? 0), {
                tone: (res?.overdue ?? 0) > 0 ? "alert" : "muted",
                icon: "running_with_errors",
              })}
              {Stat(t("reports.live.no_shows"), String(res?.no_shows ?? 0), {
                tone: (res?.no_shows ?? 0) > 0 ? "alert" : "muted",
                icon: "person_off",
              })}
            </div>
          </section>

          <section>
            <h3 class="mb-2.5 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.08em] text-accent">
              {t("reports.live.section.next")}
            </h3>
            {d?.next_arrivals?.length ? (
              <ul class="divide-y divide-base-300 overflow-hidden rounded-2xl border  bg-base-100">
                {d.next_arrivals.map((arrival, i) => (
                  <li
                    key={i}
                    class="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-base-200/40"
                  >
                    <span class="grid shrink-0 place-items-center rounded-lg bg-[#2f7ded]/12 px-2.5 py-1.5 text-sm font-bold tabular-nums text-[#4a8ff0]">
                      {arrival.time}
                    </span>
                    <span class="min-w-0 flex-1 truncate font-medium text-base-content">
                      {arrival.party_name}
                    </span>
                    <span class="shrink-0 rounded-full bg-base-200 px-2.5 py-1 text-sm font-semibold tabular-nums">
                      {t("reports.live.pax").replace(
                        "{count}",
                        String(arrival.party_size),
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <div class="rounded-2xl border border-dashed  bg-base-100/40 p-6 text-center text-sm text-base-content/70">
                {t("reports.live.no_arrivals")}
              </div>
            )}
          </section>
        </div>
      );
    };
  },
});
